/**
 * Task #9: Compresie si preview imagine client-side
 * Utility pentru compresia imaginilor inainte de upload
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeInMB?: number;
}

export interface CompressionResult {
  file: File;
  preview: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Comprima o imagine folosind Canvas API
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.85,
    maxSizeInMB = 5,
  } = options;

  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  // Verifica dimensiunea initiala
  if (file.size > maxSizeInBytes) {
    throw new Error(`Fisierul este prea mare. Dimensiunea maxima este ${maxSizeInMB}MB.`);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculeaza dimensiunile noi pastrand aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        // Creeaza canvas pentru compresie
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Nu s-a putut crea contextul canvas'));
          return;
        }

        // Deseneaza imaginea redimensionata
        ctx.drawImage(img, 0, 0, width, height);

        // Converteste la blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Nu s-a putut comprima imaginea'));
              return;
            }

            // Creeaza un nou file din blob
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            // Creeaza preview URL
            const preview = canvas.toDataURL(file.type, quality);

            resolve({
              file: compressedFile,
              preview,
              originalSize: file.size,
              compressedSize: compressedFile.size,
              compressionRatio: ((1 - compressedFile.size / file.size) * 100).toFixed(2) as any,
            });
          },
          file.type,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Nu s-a putut incarca imaginea'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Nu s-a putut citi fisierul'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Valideaza tipul fisierului imagine
 */
export const validateImageType = (file: File): boolean => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  return allowedTypes.includes(file.type);
};

/**
 * Formateaza dimensiunea fisierului pentru afisare
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Creeaza preview URL pentru imagine
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

